import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { faUser } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

export default function HotelRoomCard({
  handleClose,
  filteredHotels,
  hotel,
  inquiryForm,
  setInquiryForm,
  setCurrentHotelIndex,
  selectedRooms,
  setSelectedRooms,
  hotelModalRef,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownsOpen, setDropdownsOpen] = useState([]);

  const dropdownRef = useRef(null);
  const dropdownRoomRefs = useRef([]);

  const toggleDropdown = () => {
    setShowDropdown(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }

      // Check each room dropdown ref individually
      dropdownRoomRefs.current.forEach((ref, index) => {
        if (ref && !ref.contains(event.target)) {
          setDropdownsOpen((prev) => {
            const newState = [...prev];
            newState[index] = false;
            return newState;
          });
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const totalCapacity = selectedRooms.reduce(
    (sum, room) => sum + room.capacity,
    0
  );

  const addRoom = () => {
    const currentTotalCapacity = selectedRooms.reduce(
      (sum, room) => sum + room.capacity,
      0
    );
    const remainingGuests = inquiryForm.numberOfGuests - currentTotalCapacity;

    // Don't add room if already at or over capacity
    if (remainingGuests <= 0) return;

    const sortedRooms = [...hotel.roomTypes].sort(
      (a, b) => a.capacity - b.capacity
    );

    // Find the best fit room for remaining guests
    const bestFit = sortedRooms.find(
      (room) => room.capacity >= remainingGuests
    );

    // If no perfect fit, use the smallest room available
    const fallbackRoom = sortedRooms[0];

    const roomToAdd = bestFit || fallbackRoom;

    if (roomToAdd) {
      const newSelectedRooms = [...selectedRooms, roomToAdd];
      setSelectedRooms(newSelectedRooms);
      setInquiryForm((prev) => ({
        ...prev,
        hotelPreference: newSelectedRooms.map((room) => ({
          hotelId: hotel._id,
          roomType: room.type,
          hotelName: hotel.name,
          pricePerNight: room.pricePerNight,
          roomId: room._id,
        })),
      }));
    }
  };

  return (
    <div ref={hotelModalRef} className="absolute top-[48px] md:-right-2 md:-top-3 mt-2 z-10 bg-white p-4 rounded-lg shadow-lg w-full md:w-[330px]">

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <img
            src="/images/hotel.jpg"
            alt="Hotel"
            className="w-4 h-4 lg:w-7 lg:h-7 object-cover rounded"
          />
          <h4>{hotel.name}</h4>
          <div ref={dropdownRef}>
            <FontAwesomeIcon
              icon={!showDropdown ? faAngleDown : faAngleUp}
              className="cursor-pointer"
              onClick={toggleDropdown}
            />
            {showDropdown && (
              <div
                className="absolute top-10 left-10 bg-white shadow-md rounded-md border z-20"
              >
                {filteredHotels.map((hotel, index) => {
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentHotelIndex(index);
                        setShowDropdown(false);
                      }}
                      className="block w-full px-4 py-2 hover:bg-gray-100 text-left text-sm"
                    >
                      {hotel.name} <span className="pl-2">{hotel.markupPrice ? `+€${hotel.markupPrice}` : ""}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="pr-1 md:pr-3 cursor-pointer" onClick={handleClose}>
          <FontAwesomeIcon icon={faAngleUp} />
        </div>
      </div>
      <div className="mb-3 ml-10">
        {selectedRooms.map((room, index) => {
          const assignedCapacity = selectedRooms.reduce((sum, r, i) => {
            if (i === index) return sum;
            return sum + r.capacity;
          }, 0);
          const remainingGuests = inquiryForm.numberOfGuests - assignedCapacity;
          const toggleRoomDropdown = (index) => {
            setDropdownsOpen((prev) => {
              const newState = [...prev];
              newState[index] = !newState[index];
              return newState;
            });
          };

          return (
            <div key={index} ref={(el) => (dropdownRoomRefs.current[index] = el)} className="relative flex items-center gap-1 mb-3">
              <span className="text-sm font-semibold">Room {index + 1}:</span>

              <button
                onClick={() => toggleRoomDropdown(index)}
                className="text-sm font-semibold"
              >
                {room.type}{" "}
                {room.pricePerNight ? `+€${room.pricePerNight}` : ""}
                <FontAwesomeIcon
                  icon={dropdownsOpen[index] ? faAngleUp : faAngleDown}
                  className="ml-1 cursor-pointer"
                />
              </button>
              {/* Show Remove button only if there's more than one room */}
              {selectedRooms.length > 1 && (
                <button
                  onClick={() => {
                    const updatedRooms = [...selectedRooms];
                    updatedRooms.splice(index, 1);
                    setSelectedRooms(updatedRooms);
                    setInquiryForm((prev) => ({
                      ...prev,
                      hotelPreference: updatedRooms.map((room) => ({
                        hotelId: hotel._id,
                        roomType: room.type,
                        hotelName: hotel.name,
                        pricePerNight: room.pricePerNight,
                        roomId: room._id,
                      })),
                    }));
                  }}
                  className="text-sm font-semibold ml-2"
                >
                  Remove
                </button>
              )}
              {/* Show Add room button if there's only one room and capacity is less than guests */}
              {selectedRooms.length === 1 &&
                totalCapacity < inquiryForm.numberOfGuests && (
                  <button
                    onClick={addRoom}
                    className="text-sm font-semibold ml-2 text-opera-red"
                  >
                    Add room
                  </button>
                )}
              {dropdownsOpen[index] && (
                <div
                  className="absolute top-5 left-12 bg-white shadow-md rounded-md border z-20 w-56"
                >
                  {hotel.roomTypes.map((roomOption) => {
                    const fits = roomOption.capacity <= remainingGuests;
                    const isSelected = roomOption._id === room._id;

                    if (fits || isSelected) {
                      return (
                        <button
                          key={roomOption._id}
                          onClick={() => {
                            const updatedRooms = [...selectedRooms];
                            updatedRooms[index] = roomOption;
                            setSelectedRooms(updatedRooms);
                            setInquiryForm((prev) => ({
                              ...prev,
                              hotelPreference: updatedRooms.map((room) => ({
                                hotelId: hotel._id,
                                roomType: room.type,
                                hotelName: hotel.name,
                                pricePerNight: room.pricePerNight,
                                roomId: room._id,
                              })),
                            }));
                            setDropdownsOpen((prev) => {
                              const newState = [...prev];
                              newState[index] = false;
                              return newState;
                            });
                          }}
                          className="block w-full px-4 py-2 hover:bg-gray-100 text-left text-sm"
                        >
                          {roomOption.type}{" "}
                          {roomOption.pricePerNight
                            ? `+€${roomOption.pricePerNight}`
                            : ""}
                        </button>
                      );
                    }

                    return null;
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-2 mt-2 flex justify-between">
        <div className="flex items-center gap-3 text-black">
          <FontAwesomeIcon icon={faUser} />
          <div className="flex-1">Guests</div>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="border border-gray-900 rounded-full w-8 h-8 text-black font-semibold"
            onClick={() =>
              setInquiryForm((prev) => ({
                ...prev,
                numberOfGuests:
                  prev.numberOfGuests > 1 ? prev.numberOfGuests - 1 : 1,
              }))
            }
          >
            -
          </button>
          <p className="text-black font-bold">{inquiryForm.numberOfGuests}</p>
          <button
            type="button"
            className="border border-gray-900 rounded-full w-8 h-8 text-black font-semibold"
            onClick={() =>
              setInquiryForm((prev) => ({
                ...prev,
                numberOfGuests: prev.numberOfGuests + 1,
              }))
            }
          >
            +
          </button>
        </div>
      </div>
      {/* Show "Add another room" button only if capacity is less than guests */}
      {inquiryForm.numberOfGuests > totalCapacity && (
        <button
          onClick={addRoom}
          className="font-semibold mb-5 text-sm ml-auto block text-opera-red"
        >
          Add another room
        </button>
      )}

      {/* Show validation message if capacity doesn't match guests */}
      {totalCapacity < inquiryForm.numberOfGuests && (
        <p className="text-red-500 text-sm mb-3">
          Selected rooms can accommodate {totalCapacity} guest
          {totalCapacity !== 1 ? "s" : ""}, but you have{" "}
          {inquiryForm.numberOfGuests} guest
          {inquiryForm.numberOfGuests !== 1 ? "s" : ""}. Please add more rooms.
        </p>
      )}

      <div className="flex items-center justify-end">
        <button
          onClick={() => {
            // Validate capacity before allowing to proceed
            if (totalCapacity < inquiryForm.numberOfGuests) {
              // Don't proceed if capacity is insufficient
              return;
            }
            handleClose();
          }}
          disabled={totalCapacity < inquiryForm.numberOfGuests}
          className={`px-4 rounded-md mb-4 ${totalCapacity < inquiryForm.numberOfGuests
            ? "bg-gray-400 text-gray-600 cursor-not-allowed"
            : "bg-opera-red text-white hover:bg-opera-hover"
            }`}
        >
          Done
        </button>
      </div>
      <p className="mb-4">Need to book more than 6 rooms?</p>
    </div>
  );
}
